import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "./Button";
import Input from "./Input";
import arrowImage from "../assets/svg/arrow.svg";
import { useSelector } from "react-redux";
import useLocale from "../locales";
import { useFormik } from "formik";
import * as Yup from "yup";
import { languageList, HIGH_VALUE_CURRENCIES } from "../constants";
import i18n from "../i18n";

const prepareGoalSubmission = (values, timeUnit, settings, textData) => {
  const { language, currency, country, numberSystem } = settings;

  const target = parseFloat(values.targetAmount) || 0;
  const timeframeValue = parseFloat(values.timeframe) || 1;
  const months = timeUnit === textData.goalPlanner.years ? timeframeValue * 12 : timeframeValue;
  const current = parseFloat(values.currentSavings) || 0;
  const monthly = parseFloat(values.monthlyContribution) || 0;

  const remainingAmount = target - current;
  const totalContributions = monthly * months;
  const shortfall = remainingAmount - totalContributions;
  const requiredMonthly = remainingAmount / months;

  const payload = {
    language: `${language.value}-${language.label}`,
    locale_context: country.value,
    number_system: numberSystem,
    currency: currency.value,
    goal_name: values.goalName,
    type:
      timeUnit == "Months"
        ? "short_term"
        : timeframeValue > 3
          ? "long_term"
          : "short_term",
    target_amount: target,
    current_savings: current,
    time_period: `${timeframeValue} ${timeUnit}`,
    country: country.value,
  };

  const calculationResult = {
    targetAmount: target,
    currentSavings: current,
    remainingAmount,
    timeframeMonths: months,
    timeframeDisplay: timeframeValue,
    timeUnit,
    monthlyContribution: monthly,
    totalContributions,
    requiredMonthly,
    shortfall,
    isAchievable: shortfall <= 0,
  };

  return { payload, calculationResult };
};

const HomeCalculator = ({ selectedGoal, onSubmit, initialValues, initialTimeUnit }) => {
  const { textData } = useLocale();
  const [timeUnit, setTimeUnit] = useState(initialTimeUnit || textData.goalPlanner.years);

  const { language, currency, country, numberSystem } = useSelector((state) => state.settings);

  const targetAmountSliderRef = useRef(null);
  const timeframeSliderRef = useRef(null);
  const currentSavingsSliderRef = useRef(null);

  const targetAmountBounds = useMemo(() => {
    const isHighValue = HIGH_VALUE_CURRENCIES.includes(currency?.value || "");
    return isHighValue ? { min: 1000, max: 1000000, step: 1000 } : { min: 10000, max: 10000000, step: 10000 };
  }, [currency?.value]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        goalName: Yup.string()
          .trim()
          .required(textData.goalPlanner.enterGoalName || "Goal name is required")
          .max(50, textData.goalPlanner.goalNameMaxLength || "Goal name must be less than 50 characters"),
        targetAmount: Yup.number()
          .min(
            targetAmountBounds.min,
            (textData.goalPlanner.amountMin && textData.goalPlanner.amountMin.replace("{min}", targetAmountBounds.min.toLocaleString())) ||
              `Amount must be at least ${targetAmountBounds.min.toLocaleString()}`
          )
          .required(textData.goalPlanner.targetAmountRequired || "Target amount is required")
          .max(
            targetAmountBounds.max,
            textData.goalPlanner.targetAmountMax.replace("{max}", targetAmountBounds.max.toLocaleString()) ||
              `Target amount must be at most ${targetAmountBounds.max.toLocaleString()}`
          ),
        timeframe: Yup.number()
          .min(1, textData.goalPlanner.timePeriodMin || "Time period must be greater than 0")
          .required(textData.goalPlanner.timePeriodRequired || "Time period is required")
          .test(
            "max-timeframe",
            timeUnit === textData.goalPlanner.years
              ? textData.goalPlanner.yearsMaxError(20) || "Years cannot exceed 20"
              : textData.goalPlanner.monthsMaxError(240) || "Months cannot exceed 240",
            (value) => {
              if (value == null || value === "") return true;
              const num = Number(value);
              return timeUnit === textData.goalPlanner.years ? num <= 20 : num <= 240;
            }
          ),
        currentSavings: Yup.number()
          .min(0, textData.goalPlanner.currentSavingsMin || "Current savings cannot be negative")
          .default(0)
          .max(targetAmountBounds.max, textData.goalPlanner.currentSavingsMax || `Current savings must be less than ${(targetAmountBounds.max + 1).toLocaleString()}`)
          .test(
            "less-than-target",
            textData.goalPlanner.targetAmountHigherThanSavings || "Target amount must be higher than current savings",
            function (value) {
              const { targetAmount } = this.parent;
              return (value || 0) < (targetAmount || 0);
            }
          ),
      }),
    [timeUnit, textData, targetAmountBounds]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues || {
      goalName: selectedGoal?.name || "",
      targetAmount: 10000,
      timeframe: 1,
      currentSavings: 0,
      monthlyContribution: 0,
    },
    validationSchema,
    onSubmit: (values) => {
      const settings = { language, currency, country, numberSystem };
      const submission = prepareGoalSubmission(values, timeUnit, settings, textData);
      onSubmit({ ...submission, values, timeUnit });
    },
  });

  useEffect(() => {
    if (initialTimeUnit) {
      setTimeUnit(initialTimeUnit);
    }
  }, [initialTimeUnit]);

  useEffect(() => {
    const v = formik.values.targetAmount;
    if (v > targetAmountBounds.max || v < targetAmountBounds.min) {
      formik.setFieldValue("targetAmount", Math.min(targetAmountBounds.max, Math.max(targetAmountBounds.min, v)));
    }
  }, [currency?.value]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/[^0-9]/g, "");
    formik.setFieldValue(name, numericValue ? parseFloat(numericValue) : "");
    if (name === "currentSavings" || name === "targetAmount") {
      formik.validateField("currentSavings");
    }
  };

  const handleSliderChange = (name) => (e) => {
    const value = parseFloat(e.target.value);
    formik.setFieldValue(name, value);
    if (name === "currentSavings" || name === "targetAmount") {
      formik.validateField("currentSavings");
    }
  };

  const toggleTimeUnit = () => {
    const currentValue = formik.values.timeframe;
    if (timeUnit === textData.goalPlanner.years) {
      setTimeUnit(textData.goalPlanner.months);
      const months = Math.min(6, currentValue * 12);
      formik.setFieldValue("timeframe", months);
    } else {
      setTimeUnit(textData.goalPlanner.years);
      const years = Math.min(50, Math.max(1, Math.round(currentValue / 12)));
      formik.setFieldValue("timeframe", years);
    }
  };

  const getTimeSliderProps = () => {
    if (timeUnit === textData.goalPlanner.years) {
      return { min: 1, max: 20, step: 1 };
    }
    return { min: 6, max: 240, step: 1 };
  };

  const formatCurrency = (value) => {
    if (!value) return "";
    return parseFloat(value).toLocaleString("en-US");
  };

  const currentCurrency = useMemo(() => {
    return languageList.find((lang) => lang.currencyCode === currency.value)?.currency || currency.symbol;
  }, [currency]);

  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const handlePlanMyGoal = (e) => {
    e.preventDefault();
    const target = Number(formik.values.targetAmount) || 0;
    const savings = Number(formik.values.currentSavings) || 0;
    if (target <= savings) {
      formik.setFieldError(
        "currentSavings",
        textData.goalPlanner.targetAmountHigherThanSavings || "Target amount must be higher than current savings"
      );
      formik.setFieldTouched("currentSavings", true);
      return;
    }
    formik.handleSubmit();
  };

  return (
    <form onSubmit={handlePlanMyGoal} className="w-full space-y-4">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-start font-normal text-black text-sm">{textData.goalPlanner.targetAmount}</label>
        </div>
        <div className="flex justify-between gap-5 items-center">
          <div className="relative w-full" ref={targetAmountSliderRef}>
            <input
              type="range"
              min={targetAmountBounds.min}
              max={targetAmountBounds.max}
              step={targetAmountBounds.step}
              value={Math.min(
                targetAmountBounds.max,
                Math.max(targetAmountBounds.min, Number(formik.values.targetAmount) || targetAmountBounds.min)
              )}
              onChange={handleSliderChange("targetAmount")}
              style={{
                width: "100%",
                appearance: "none",
                background: "transparent",
                "--val": `${Math.min(
                  100,
                  Math.max(
                    0,
                    ((Number(formik.values.targetAmount) || 0) - targetAmountBounds.min) /
                      (targetAmountBounds.max - targetAmountBounds.min) *
                      100
                  )
                )}%`,
                ...(isRTL() && { direction: "ltr" }),
              }}
            />
          </div>
          <div className="flex flex-col">
            <Input
              name="targetAmount"
              leftIcon={currentCurrency}
              value={formatCurrency(formik.values.targetAmount)}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              type="text"
              dir="ltr"
              placeholder={targetAmountBounds.min.toLocaleString()}
              className="sm:h-[36px] h-[32px] border border-primary border-opacity-50 flex items-center py-[7.5px] rounded-full focus:outline-none mt-0 px-[8px] sm:px-[12px] w-[112px] sm:w-[156px]"
            />
            {formik.touched.targetAmount && formik.errors.targetAmount && (
              <p className="text-red-500 text-xs mt-1 text-center whitespace-nowrap">{formik.errors.targetAmount}</p>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-1 flex-row">
            <label className="text-start font-normal text-black text-sm">{textData.goalPlanner.timePeriod}</label>
            <div
              onClick={toggleTimeUnit}
              className="ml-[10px] cursor-pointer items-center flex gap-[2px] text-primary hover:text-primary-dark text-sm font-medium transition focus:outline-none"
            >
              {timeUnit}
              <img src={arrowImage} alt="Dropdown arrow" className="w-3 h-3" />
            </div>
          </div>
        </div>
        <div className="flex justify-between gap-5 items-center">
          <div className="relative w-full" ref={timeframeSliderRef}>
            <input
              type="range"
              min={getTimeSliderProps().min}
              max={getTimeSliderProps().max}
              step={getTimeSliderProps().step}
              value={formik.values.timeframe}
              onChange={handleSliderChange("timeframe")}
              style={{
                width: "100%",
                appearance: "none",
                background: "transparent",
                "--val": `${
                  ((formik.values.timeframe - getTimeSliderProps().min) /
                    (getTimeSliderProps().max - getTimeSliderProps().min)) *
                  100
                }%`,
                ...(isRTL() && { direction: "ltr" }),
              }}
            />
          </div>
          <div className="flex flex-col">
            <Input
              name="timeframe"
              leftIcon={timeUnit === textData.goalPlanner.years ? "Yr" : "M"}
              value={formik.values.timeframe}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              type="number"
              placeholder={timeUnit === textData.goalPlanner.years ? "10" : "120"}
              className="sm:h-[36px] h-[32px] border border-primary border-opacity-50 flex items-center py-[7.5px] rounded-full focus:outline-none mt-0 px-[8px] sm:px-[12px] w-[112px] sm:w-[156px]"
            />
            {formik.touched.timeframe && formik.errors.timeframe && (
              <p className="text-red-500 text-xs mt-1 text-center whitespace-nowrap">{formik.errors.timeframe}</p>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-start font-normal text-black text-sm">{textData.goalPlanner.currentSavings}</label>
        </div>
        <div className="flex justify-between gap-5 items-center">
          <div className="relative w-full" ref={currentSavingsSliderRef}>
            <input
              type="range"
              min={0}
              max={targetAmountBounds.max}
              step={Math.min(1000, targetAmountBounds.step)}
              value={formik.values.currentSavings}
              onChange={handleSliderChange("currentSavings")}
              style={{
                width: "100%",
                appearance: "none",
                background: "transparent",
                "--val": `${(formik.values.currentSavings / targetAmountBounds.max) * 100}%`,
                ...(isRTL() && { direction: "ltr" }),
              }}
            />
          </div>
          <div className="flex flex-col">
            <Input
              name="currentSavings"
              leftIcon={currentCurrency}
              value={formatCurrency(formik.values.currentSavings)}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              type="text"
              dir="ltr"
              placeholder="0"
              className="sm:h-[36px] h-[32px] border border-primary border-opacity-50 flex items-center py-[7.5px] rounded-full focus:outline-none mt-0 px-[8px] sm:px-[12px] w-[112px] sm:w-[156px]"
            />
          </div>
        </div>
        {formik.errors.currentSavings &&
          (formik.touched.currentSavings || formik.touched.targetAmount || formik.submitCount > 0) && (
            <p className="text-red-500 text-xs mt-1 text-end whitespace-nowrap">{formik.errors.currentSavings}</p>
          )}
      </div>

      <div className="border-t border-t-[#DDE1E5] h-[1px] w-full !my-8" />
      <Button type="submit" className="w-full rounded-full font-lato-semibold-600 text-base transition-colors !mt-0">
        {textData.goalPlanner.planBtn}
      </Button>
    </form>
  );
};

export default HomeCalculator;
